import { DocumentTypeLabelPipe } from '../../pipes/document-type-label.pipe';
import { getDocumentTypeLabel } from '../../helpers/document-type-label.helper';
import { Component, EventEmitter, inject, Input, OnInit, Output, WritableSignal } from '@angular/core';
import { differenceInDays, endOfDay } from 'date-fns';
import { isExpired, isExpiringSoon } from '../../../../_shared/helpers/license-status.helper';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DriverDocuments, DocumentType, DeleteDriverDocument } from '../../models/driver-documents.model';
import { DriverDocumentsService } from '../../services/driver-documents.service';
import { FormsModule } from '@angular/forms';
import { EditDriverDocumentModalComponent } from '../modals/edit-driver-document-modal/edit-driver-document-modal.component';
import { UploadDriverDocumentsModalComponent } from '../modals/upload-driver-documents-modal/upload-driver-documents-modal.component';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';
import { ConfirmationModalService } from '../../../../_shared/services/modals/confirmation-modal.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { DriverDocumentsListService } from '../../services/driver-documents-list.service';
import { Subscription } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-documents-details',
  standalone: true,
  imports: [DocumentTypeLabelPipe, CommonModule, NgbTooltip, FormsModule, TranslocoDirective],
  templateUrl: './driver-documents-details.component.html',
  styleUrl: './driver-documents-details.component.scss'
})
export class DriverDocumentsDetailsComponent implements OnInit {
  @Input() driverId: number;
  @Output() loaded = new EventEmitter<boolean>();
  @Input() hasDrivers: WritableSignal<boolean | null>;

  isLoading = false;
  isDriverSelected: boolean = false;
  driverDocuments: DriverDocuments[] = [];
  documentExpired: boolean = false;
  documentExpiringSoon: boolean = false;
  documentNotificationMessage: string = '';
  documentTypes: DocumentType[] = [];
  hasProfessionalDrivingPermit: boolean = false;

  private driverDocumentsService = inject(DriverDocumentsService);
  private driverListService = inject(DriverDocumentsListService);
  private modalService = inject(NgbModal);
  private confirmationModalService = inject(ConfirmationModalService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  private driverSubscription: Subscription;

  ngOnInit(): void {
    this.getDocumentTypes();

    this.driverSubscription = this.driverListService.selectedDriver$.subscribe(driver => {
      if (driver?.id) {
        this.driverId = driver.id;
        this.isDriverSelected = true;
        this.getDriverDocuments(driver.id);
        this.hasDrivers.set(true);
      } else {
        this.isDriverSelected = false;
        this.hasDrivers.set(false);
        this.loaded.emit(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.driverSubscription.unsubscribe();
  }

  isDocumentExpired(validUntil: Date): boolean | null {
    if (!validUntil) {
      return null;
    }

    this.documentExpired = isExpired(validUntil)!;
    if (this.documentExpired) {
      this.documentNotificationMessage = this.transloco.translate('driverDocuments.details.documentExpired');
    }

    return this.documentExpired;
  }

  isDocumentExpiringSoon(validUntil: Date): boolean | null {
    if (!validUntil) {
      return null;
    }

    const expiringSoon = isExpiringSoon(validUntil);
    if (expiringSoon === null) {
      return null;
    }

    if (expiringSoon) {
      const renewalDate = validUntil;
      const today = endOfDay(new Date());
      const daysUntilExpiration = differenceInDays(endOfDay(renewalDate), today);

      this.documentNotificationMessage = daysUntilExpiration === 0
        ? this.transloco.translate('driverDocuments.details.expiringToday')
        : this.transloco.translate('driverDocuments.details.expiringInDays', { days: daysUntilExpiration });

      return true;
    }

    return false;
  }

  async getDocumentTypes() {
    try {
      const response = await this.driverDocumentsService.getDocumentTypes();
      this.documentTypes = response.data || [];
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async getDriverDocuments(id: number) {
    try {
      this.isLoading = true;
      const response = await this.driverDocumentsService.getDriverDocuments(id);
      this.driverDocuments = response.data || [];
      this.hasProfessionalDrivingPermit = this.driverDocuments.some(doc => doc.documentName === 'Professional Driving Permit');

      this.isLoading = false;
      this.loaded.emit(true);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async downloadDocument(doc: DriverDocuments) {
    const a = document.createElement('a');
    a.href = doc.documentLink;
    a.setAttribute('download', `${doc.documentName}.${doc.extension}`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async deleteDriverDocument(document: DeleteDriverDocument) {
    try {
      await this.driverDocumentsService.deleteDriverDocument(document);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  openUploadDriverDocumentModal() {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'custom-modal-window'
    };

    const modalRef = this.modalService.open(UploadDriverDocumentsModalComponent, modalOptions);
    modalRef.componentInstance.documentTypes = this.documentTypes;
    modalRef.componentInstance.driverId = this.driverId;
    modalRef.componentInstance.uploadedDocumentsDetails = { 
      names: this.driverDocuments.map(doc => doc.documentName || ''), 
      hasLicensePdp: this.driverDocuments.find(doc => doc.documentName === 'Drivers License')?.pdpValidUntil || false 
    };

    modalRef.closed.subscribe(() => {
      this.getDriverDocuments(this.driverId);
      this.driverListService.refreshAlerts(this.driverId);
    });
  }

  openEditDriverDocumentModal(document: DriverDocuments) {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'custom-modal-window'
    };

    const modalRef = this.modalService.open(EditDriverDocumentModalComponent, modalOptions);
    modalRef.componentInstance.document = document;
    modalRef.componentInstance.documentTypes = this.documentTypes;
    modalRef.componentInstance.hasProfessionalDrivingPermit = this.hasProfessionalDrivingPermit;

    modalRef.closed.subscribe(() => {
      this.getDriverDocuments(this.driverId);
      this.driverListService.refreshAlerts(this.driverId);
    });
  }

  openDeleteDriverDocumentConfirmationModal(document: DriverDocuments) {
    const payload = {
      documentId: document.documentId,
      driverId: this.driverId,
      hasLicensePdp: (document.documentName === 'Drivers License' && document.pdpValidUntil) ? true : false
    }

    const options: ConfirmationOptions = {
      title: this.transloco.translate('driverDocuments.deletePrompt.title'),
      message: this.transloco.translate('driverDocuments.deletePrompt.message', { name: getDocumentTypeLabel(this.transloco, document.documentName) }),
      warningMessage: this.transloco.translate('driverDocuments.deletePrompt.warning', { name: getDocumentTypeLabel(this.transloco, document.documentName) }),
      confirmText: this.transloco.translate('driverDocuments.deletePrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline'),
      onConfirm: () => this.deleteDriverDocument(payload)
    }

    this.confirmationModalService.showConfirmationModal(options).then(async (confirmed) => {
      if (confirmed) {
        this.getDriverDocuments(this.driverId);
        this.driverListService.refreshAlerts(this.driverId);
      }
    });
  }
}